// Q9 S5 / D12 — Failed-save retry surface.
//
// Spec (docs/specs/visit-report-save-state.md §Save failure handling):
//   PATCH fails → indicator shows "Save failed — retrying…" → wizard nav
//   stays unblocked → background retry queue retries with exponential
//   backoff up to 4 attempts → if exhausted, persist to IDB +
//   surface a manual-retry affordance (this component).
//
// Three failure surfaces, distinguished by FailedSection.permanent +
// FailedSection.exhausted:
//   permanent  — 422 validation or 4xx client error. No retry path
//                will help; the carer must fix the data. Surfaces with
//                "Couldn't save" wording (no retry button).
//   exhausted  — Retried 4× with no success. Persisted to IDB; will
//                replay automatically next online event, OR the carer
//                can click Retry to bypass the wait.
//   in-flight  — Retry in progress. Shows attempt count.

import type { JSX } from "react";
import type { SectionStep } from "../lib/visit-report-conditional-rules.js";
import type { FailedSection } from "../hooks/use-auto-save.js";
import { MAX_RETRY_ATTEMPTS } from "../hooks/use-auto-save.js";

const SECTION_LABELS: Record<SectionStep, string> = {
  vitals:     "Vitals",
  mobility:   "Mobility",
  clinical:   "Clinical",
  wellbeing:  "Wellbeing",
  medication: "Medication",
  notes:      "Notes",
};

interface Props {
  failedSections: ReadonlyMap<SectionStep, FailedSection>;
  onManualRetry: (section: SectionStep) => void;
}

function describeFailure(info: FailedSection): { tone: "warn" | "error"; text: string; showRetry: boolean } {
  if (info.permanent) {
    return { tone: "error", text: `Couldn't save: ${info.lastError}`, showRetry: false };
  }
  if (info.exhausted) {
    return {
      tone: "error",
      text: `Save persistently failing — saved locally and will retry when online (${MAX_RETRY_ATTEMPTS} attempts exhausted).`,
      showRetry: true,
    };
  }
  return {
    tone: "warn",
    text: `Save failed (attempt ${info.attemptCount}/${MAX_RETRY_ATTEMPTS}) — retrying…`,
    showRetry: false,
  };
}

export function RetryIndicator({ failedSections, onManualRetry }: Props): JSX.Element | null {
  if (failedSections.size === 0) return null;

  return (
    <div role="alert" data-testid="retry-indicator" className="mb-4 space-y-2">
      {[...failedSections.entries()].map(([section, info]) => {
        const { tone, text, showRetry } = describeFailure(info);
        const cls = tone === "error"
          ? "bg-red-50 border border-red-200 text-red-700"
          : "bg-amber-50 border border-amber-200 text-amber-800";
        return (
          <div
            key={section}
            data-testid={`retry-row-${section}`}
            data-tone={tone}
            className={`text-sm rounded-lg p-3 flex items-start justify-between gap-3 ${cls}`}
          >
            <div className="flex-1 min-w-0">
              <p className="font-medium">{SECTION_LABELS[section]}</p>
              <p className="text-xs mt-0.5">{text}</p>
            </div>
            {showRetry && (
              <button
                type="button"
                onClick={() => onManualRetry(section)}
                data-testid={`retry-button-${section}`}
                className="px-3 py-1 text-xs font-medium rounded border border-red-300 hover:bg-red-100 transition-colors"
              >
                Retry
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
